import { useCallback, useMemo } from 'react'
import { config } from '../../../../App'
import {
  APPAREL_AND_ACCESSORIES,
  ESSENTIALS_AND_BEAUTY,
  FLORAL,
  GROCERY,
  HOME_AND_HARDLINES,
  LOCATION_RFI_APPROVED,
  LOCATION_RFI_REJECTED,
  LOCATION_RFI_SENT_BACK,
  MANUFACTURING_TYPE_ID_FABRIC,
  MANUFACTURING_TYPE_ID_FINISHED_GOOD,
  MANUFACTURING_TYPE_ID_PACKAGING,
  MANUFACTURING_TYPE_ID_RAWMATERIAL,
  MANUFACTURING_TYPE_ID_SECONDARY_PROCESSING,
  MANUFACTURING_TYPE_ID_TRIM,
  RFI_TYPE_LOCATION,
  RFI_TYPE_QUESTIONNAIRE,
  RFI_TYPE_SUPPLIER,
  STATUS_APPROVE_AND_START_VFA,
  STATUS_APPROVED,
  STATUS_COMPLETE_REVIEW,
  STATUS_REJECTED,
  STATUS_SENT_BACK,
  STATUS_SUBMITTED,
  SUPPLIER_RFI_APPROVED,
  SUPPLIER_RFI_NOT_APPROVED,
  SUPPLIER_RFI_REJECTED,
  SUPPLIER_RFI_REJECTED_FOR_RFI2,
  SUPPLIER_RFI_SENT_BACK,
} from '../../../../common/commonConstants'
import { getVettingStatusCodeFromStatusValue } from '../../../../common/helper'
import {
  postRfiAction,
  saveCapabilitiesAndComments,
  setRFI2PrefillPopup,
} from '../../../../services/lcmService'
import InternalApprovalButtons from '../components/InternalApprovalButtons'
import { TYPES as DT } from '../context/DataContext'
import { TYPES as UT } from '../context/UiContext'
import { useEnv } from '@praxis/component-runtime-env'

export default function useRFIApprovalActions({
  d,
  u,
  dd,
  ud,
  rfiType,
  manufacturingTypeId,
  userSettings,
  fetchAll,
  publishRFITimelineData,
  pushDataToFirefly,
  setRefreshRFIScreen,
}) {
  const  env  = useEnv() 
  const showTooltipForButton = useCallback(
    (btnType) => {
      const currentVettingStatus = String(
        d?.rfiObject?.vetting_status || '',
      ).toLowerCase()

      const supplierVettingStatus = String(
        d?.rfiObject?.supplier_vetting_status || '',
      ).toLowerCase()

      const isSupplierRfi = rfiType === RFI_TYPE_SUPPLIER
      const isLocationRfi = rfiType === RFI_TYPE_LOCATION

      if (isSupplierRfi) {
        if (currentVettingStatus === STATUS_APPROVED) {
          return {
            showTooltip: true,
            errorMsg: SUPPLIER_RFI_APPROVED,
          }
        }

        if (currentVettingStatus === STATUS_REJECTED) {
          return {
            showTooltip: true,
            errorMsg: SUPPLIER_RFI_REJECTED,
          }
        }

        if (currentVettingStatus === STATUS_SENT_BACK) {
          return {
            showTooltip: true,
            errorMsg: SUPPLIER_RFI_SENT_BACK,
          }
        }
      }

      if (isLocationRfi) {
        if (currentVettingStatus === STATUS_APPROVED) {
          return {
            showTooltip: true,
            errorMsg: LOCATION_RFI_APPROVED,
          }
        }

        if (currentVettingStatus === STATUS_REJECTED) {
          return {
            showTooltip: true,
            errorMsg: LOCATION_RFI_REJECTED,
          }
        }

        if (currentVettingStatus === STATUS_SENT_BACK) {
          return {
            showTooltip: true,
            errorMsg: LOCATION_RFI_SENT_BACK,
          }
        }
      }

      if (
        isLocationRfi &&
        currentVettingStatus === STATUS_SUBMITTED &&
        d?.rfiObject?.supplier_migrated !== true &&
        supplierVettingStatus !== STATUS_APPROVED
      ) {
        if (supplierVettingStatus === STATUS_REJECTED) {
          return {
            showTooltip: true,
            errorMsg: SUPPLIER_RFI_REJECTED_FOR_RFI2,
          }
        } else if (['approve', 'reject', 'complete_review'].includes(btnType)) {
          return {
            showTooltip: true,
            errorMsg: SUPPLIER_RFI_NOT_APPROVED,
          }
        }
      }

      return { showTooltip: false, errorMsg: '' }
    },
    [d?.rfiObject, rfiType],
  )

  const approvalHooks = useMemo(
    () => ({
      setUserCommentsValue: (e, maxLen) => {
        const v = e?.target?.value || ''
        if (v.length <= (maxLen || 2000)) {
          ud({ type: UT.SET_ACTION, payload: { userComments: v } })
        }
      },
      closeRFIActionConfirmationModal: () => {
        ud({ type: UT.SET_LOADING, payload: { action: false } })
        ud({ type: UT.SET_ACTION_FAILED, payload: false })
        ud({
          type: UT.SET_ACTION,
          payload: { userAction: '', userComments: '' },
        })
        ud({ type: UT.SET_MODAL, payload: { actionConfirmation: false } })
      },
      closeRFIPrefillModal: async () => {
        ud({ type: UT.SET_MODAL, payload: { prefill: false } })
        try {
          await setRFI2PrefillPopup({
            supplier_id: d?.rfiObject?.supplier_id,
            location_id: d?.rfiObject?.location_id,
            manufacturing_type_id: d?.rfiObject?.manufacturing_type_id,
            manufacturing_type: d?.rfiObject?.manufacturing_type,
            show_prefill_popup: false,
          })
        } catch {
          // swallow
        }
      },
      onCapabilityNextActionConfirmation: async (
        newComments,
        newCapabilities,
        isCapabilityManagementMode,
      ) => {
        try {
          ud({ type: UT.SET_LOADING, payload: { nextActionPC: true } })
          ud({ type: UT.SET_NEXT_ACTION_PC_FAILED, payload: false })
          const isPackaging =
            Number(manufacturingTypeId) === Number(MANUFACTURING_TYPE_ID_PACKAGING)
          const body = {
            vetting_type: isPackaging ? RFI_TYPE_QUESTIONNAIRE : rfiType,
            supplier_id: d?.rfiObject?.supplier_id,
            supplier_name:
              d?.rfiObject?.fixed_sections?.supplier_contact_information
                ?.supplier_name,
            manufacturing_type_id: manufacturingTypeId,
            manufacturing_type: d?.rfiObject?.manufacturing_type,
            vetting_status_comments: newComments,
            capability_vetting_status: newCapabilities,
            ...(rfiType === RFI_TYPE_LOCATION && {
              location_id: d?.rfiObject?.location_id,
              location_name:
                d?.rfiObject?.fixed_sections?.factory_contact_information
                  ?.location_name,
            }),
          }
          await saveCapabilitiesAndComments(body)
          ud({ type: UT.SET_LOADING, payload: { nextActionPC: false } })

          if (isCapabilityManagementMode) {
            ud({
              type: UT.SET_MODAL,
              payload: {
                productCapabilities: { isVisible: false, isReadOnly: false },
              },
            })
            setRefreshRFIScreen((prev) => !prev)
          } else {
            ud({
              type: UT.SET_MODAL,
              payload: {
                productCapabilities: { isVisible: false, isReadOnly: false },
                email: true,
              },
            })
          }

          ud({ type: UT.SET_ACTION, payload: { userComments: newComments } })
          dd({
            type: DT.SET_DATA,
            payload: {
              ...d.rfiObject,
              vetting_status_comments: newComments,
              capability_vetting_status: newCapabilities,
            },
          })
        } catch {
          ud({ type: UT.SET_LOADING, payload: { nextActionPC: false } })
          ud({ type: UT.SET_NEXT_ACTION_PC_FAILED, payload: true })
        }
      },
      closeEmailDialogForApprovalActions: (
        launchNext,
        isSaveAndClose = false,
      ) => {
        ud({ type: UT.SET_MODAL, payload: { email: false } })
        // this mirrors old closeEmailDialogForApprovalActions
        if (launchNext && !isSaveAndClose) {
          const shouldLaunchCTV =
            rfiType === RFI_TYPE_SUPPLIER &&
            Number(d?.rfiObject?.manufacturing_type_id) ===
              Number(MANUFACTURING_TYPE_ID_FINISHED_GOOD) &&
            String(u.userAction || '').toUpperCase() ===
              String(STATUS_APPROVED).toUpperCase() &&
            (
              d?.rfiObject?.dynamic_sections?.business_capability
                ?.total_product_capabilities?.total_product_capability_list ||
              []
            ).some((totalProductCapability) =>
              (
                totalProductCapability?.product_capability?.group_names || []
              ).some((groupName) =>
                [
                  APPAREL_AND_ACCESSORIES,
                  HOME_AND_HARDLINES,
                  ESSENTIALS_AND_BEAUTY,
                ].includes(groupName),
              ),
            ) &&
            !u.isNBIOR &&
            d?.rfiObject?.first_time_approval

          if (shouldLaunchCTV) {
            ud({ type: UT.SET_MODAL, payload: { ctvContacts: true } })
          } else {
            ud({
              type: UT.SET_MODAL,
              payload: { actionConfirmation: true },
            })
          }
        } else if (launchNext) {
          ud({
            type: UT.SET_FLAGS,
            payload: { isSaveAndCloseClicked: isSaveAndClose },
          })
          ud({ type: UT.SET_LOADING, payload: { action: false } })
          ud({ type: UT.SET_ACTION_FAILED, payload: false })
        } else {
          ud({ type: UT.SET_LOADING, payload: { action: false } })
          ud({ type: UT.SET_ACTION_FAILED, payload: false })
        }
      },
      closeCTPContactsDialog: (launchNext, isSaveAndClose = false) => {
        ud({ type: UT.SET_MODAL, payload: { ctpContacts: false } })
        if (launchNext && !isSaveAndClose) {
          // After CTP modal, show email modal
          ud({ type: UT.SET_MODAL, payload: { email: true } })
        } else if (launchNext) {
          ud({
            type: UT.SET_FLAGS,
            payload: { isSaveAndCloseClicked: isSaveAndClose },
          })
          ud({ type: UT.SET_LOADING, payload: { action: false } })
          ud({ type: UT.SET_ACTION_FAILED, payload: false })
        } else {
          ud({ type: UT.SET_LOADING, payload: { action: false } })
          ud({ type: UT.SET_ACTION_FAILED, payload: false })
        }
      },
      closeCTVContactsDialog: (launchConfirm, isSaveAndClose = false) => {
        ud({ type: UT.SET_MODAL, payload: { ctvContacts: false } })
        if (launchConfirm && !isSaveAndClose) {
          ud({ type: UT.SET_MODAL, payload: { actionConfirmation: true } })
        } else if (launchConfirm) {
          ud({
            type: UT.SET_FLAGS,
            payload: { isSaveAndCloseClicked: isSaveAndClose },
          })
          ud({ type: UT.SET_LOADING, payload: { action: false } })
          ud({ type: UT.SET_ACTION_FAILED, payload: false })
        } else {
          ud({ type: UT.SET_LOADING, payload: { action: false } })
          ud({ type: UT.SET_ACTION_FAILED, payload: false })
        }
      },
      onActionConfirmation: async () => {
        try {
          ud({ type: UT.SET_LOADING, payload: { action: true } })
          ud({ type: UT.SET_ACTION_FAILED, payload: false })
          ud({ type: UT.SET_MODAL, payload: { actionConfirmation: false } })

          const baseBody = {
            vetting_type: rfiType,
            supplier_id: d?.rfiObject?.supplier_id,
            supplier_name:
              d?.rfiObject?.fixed_sections?.supplier_contact_information
                ?.supplier_name,
            manufacturing_type_id: manufacturingTypeId,
            manufacturing_type: d?.rfiObject?.manufacturing_type,
            vetting_status_comments:
              Number(manufacturingTypeId) ===
                Number(MANUFACTURING_TYPE_ID_FINISHED_GOOD) &&
              [
                STATUS_APPROVED,
                STATUS_COMPLETE_REVIEW,
                STATUS_APPROVE_AND_START_VFA,
              ]
                .map((s) => String(s || '').toUpperCase())
                .includes(String(u.userAction || '').toUpperCase())
                ? ''
                : u.userComments,
            ...(rfiType === RFI_TYPE_LOCATION && {
              location_id: d?.rfiObject?.location_id,
              location_name:
                d?.rfiObject?.fixed_sections?.factory_contact_information
                  ?.location_name,
            }),
            ...((Number(manufacturingTypeId) ===
              Number(MANUFACTURING_TYPE_ID_FABRIC) ||
              Number(manufacturingTypeId) ===
                Number(MANUFACTURING_TYPE_ID_TRIM) ||
              Number(manufacturingTypeId) ===
                Number(MANUFACTURING_TYPE_ID_RAWMATERIAL) ||
              Number(manufacturingTypeId) ===
                Number(MANUFACTURING_TYPE_ID_SECONDARY_PROCESSING)) && {
              capability_vetting_status:
                d?.rfiObject?.capability_vetting_status,
            }),
            review_status: u.userAction,
            source: d?.rfiObject?.source,
          }

          const vettingStatusCodeToSend =
            await getVettingStatusCodeFromStatusValue(
              String(u.userAction || '').toUpperCase(),
            )

          const rfi3Required =
            d?.template?.rfi3_required == null
              ? true
              : d?.template?.rfi3_required

          const body = {
            ...baseBody,
            vetting_status_code: vettingStatusCodeToSend,
            vetted_by_id: config.headers['User-Id'],
            vetted_by_email: config.headers['User-Email'],
            vetted_by_name: config.headers['User-Name'],
            rfi3_required: rfi3Required,
          }

          publishRFITimelineData(vettingStatusCodeToSend)

          const rfiActionResponse = await postRfiAction(body)
          if (!rfiActionResponse) return

          ud({
            type: UT.SET_ACTION,
            payload: { userAction: '', userComments: '' },
          })
          ud({ type: UT.SET_LOADING, payload: { action: false } })
          ud({ type: UT.SET_ACTION_FAILED, payload: false })

          if (
            String(
              d?.rfiObject?.last_vetting_status?.vetting_status || '',
            ).toLowerCase() === String(STATUS_SUBMITTED).toLowerCase()
          ) {
            pushDataToFirefly(
              String(u.userAction || '').toLowerCase(),
              STATUS_SUBMITTED,
            )
          }
          setRefreshRFIScreen(true)
        } catch {
          ud({ type: UT.SET_LOADING, payload: { action: false } })
          ud({ type: UT.SET_ACTION_FAILED, payload: true })
          setRefreshRFIScreen(false)
        }
      },
    }),
    [
      d?.rfiObject,
      d?.template,
      manufacturingTypeId,
      rfiType,
      u.userAction,
      u.userComments,
      u.isNBIOR,
      ud,
      dd,
      publishRFITimelineData,
      pushDataToFirefly,
      setRefreshRFIScreen,
    ],
  )

  const onApproveClick = useCallback(
    ({ action, manufacturingTypeId: mId, hasRenderableCapabilities }) => {
      ud({ type: UT.SET_ACTION, payload: { userAction: action } })
      
      // Exclude when factory capabilities have ONLY FLORAL group names
      // or ONLY Grocery product types
      const factoryCapabilityList =
        d?.rfiObject?.dynamic_sections?.business_capability
          ?.factory_capabilities?.factory_capability_list || []

      const factoryGroupNames = factoryCapabilityList
        .flatMap((fc) => fc?.factory_capability?.group_names || [])
        .map((groupName) => String(groupName || '').toUpperCase())
        .filter(Boolean)

      const factoryProductTypes = factoryCapabilityList
        .map((fc) =>
          String(fc?.factory_capability?.product_type || '').toUpperCase(),
        )
        .filter(Boolean)

      const hasOnlyFloralGroup =
        factoryGroupNames.length > 0 &&
        factoryGroupNames.every((groupName) => groupName === FLORAL)

      const hasOnlyGroceryProductType =
        factoryProductTypes.length > 0 &&
        factoryProductTypes.every((productType) => productType === GROCERY)

      const hasExcludedFactoryGroup =
        hasOnlyFloralGroup || hasOnlyGroceryProductType

      // Launch CTP modal for Location RFI approval (finished goods or secondary processing)
      const isCTPModalEnabled = env?.mfelcm?.enableCTPModal !== false
      const shouldLaunchCTP =
        isCTPModalEnabled &&
        rfiType === RFI_TYPE_LOCATION &&
        String(action || '').toUpperCase() ===
          String(STATUS_APPROVED).toUpperCase() &&
        ((Number(mId) === Number(MANUFACTURING_TYPE_ID_FINISHED_GOOD) &&
          !hasExcludedFactoryGroup) ||
          Number(mId) === Number(MANUFACTURING_TYPE_ID_SECONDARY_PROCESSING))
      if (shouldLaunchCTP) {
        ud({ type: UT.SET_MODAL, payload: { ctpContacts: true } })
      } else if (d?.template?.show_approval_popup) {
        const isRM = Number(mId) === Number(MANUFACTURING_TYPE_ID_RAWMATERIAL)
        if (isRM && !hasRenderableCapabilities) {
          ud({ type: UT.SET_MODAL, payload: { actionConfirmation: true } })
        } else {
          ud({
            type: UT.SET_MODAL,
            payload: {
              productCapabilities: { isVisible: true, isReadOnly: false },
            },
          })
        }
      } else {
          ud({ type: UT.SET_MODAL, payload: { email: true } })
      }
    },
    [d?.template, ud, rfiType, d?.rfiObject],
  )

  const renderInternalApprovalButtons = useCallback(() => {
    return (
      <InternalApprovalButtons
        rfiType={rfiType}
        manufacturingTypeId={manufacturingTypeId}
        showTooltipForButton={showTooltipForButton}
        onApproveClick={onApproveClick}
        userSettings={userSettings}
      />
    )
  }, [
    rfiType,
    manufacturingTypeId,
    showTooltipForButton,
    onApproveClick,
    userSettings,
  ])

  return { approvalHooks, renderInternalApprovalButtons }
}
